import type { WineOrder } from "./document";

// 虛構客戶與示範酒款；不使用員工或實際客戶的個人資料。
const customers = [
  { name: "陳怡安", contact: "chen.yian@example.com" },
  { name: "林柏宇", contact: "lin.boyu@example.com" },
  { name: "張雅涵", contact: "chang.yahan@example.com" },
  { name: "王品蓉", contact: "wang.pinrong@example.com" },
  { name: "李承恩", contact: "lee.chengen@example.com" },
  { name: "許庭瑋", contact: "hsu.tingwei@example.com" },
  { name: "沐光餐飲有限公司", contact: "orders.muguang@example.com" },
  { name: "禾境宴會會館", contact: "orders.hejing@example.com" },
];

const wines = [
  { sku: "FR-BDX-2020", product: "波爾多精選紅葡萄酒", origin: "法國・波爾多", vintage: "2020", volume: "750 ml", alcohol: "13.5%", price: 2800, description: "黑醋栗與成熟莓果香氣，單寧細緻，適合搭配牛排與熟成起司。" },
  { sku: "FR-BGN-2021", product: "勃根地黑皮諾紅葡萄酒", origin: "法國・勃根地", vintage: "2021", volume: "750 ml", alcohol: "13%", price: 4200, description: "紅櫻桃與花香交織，酒體優雅，適合搭配烤鴨與菇類料理。" },
  { sku: "IT-TSC-2019", product: "托斯卡尼珍藏紅葡萄酒", origin: "義大利・托斯卡尼", vintage: "2019", volume: "750 ml", alcohol: "14%", price: 3600, description: "成熟櫻桃與香料風味，結構飽滿，適合搭配燉肉與番茄義大利麵。" },
  { sku: "ES-RIO-2020", product: "里奧哈陳年紅葡萄酒", origin: "西班牙・里奧哈", vintage: "2020", volume: "750 ml", alcohol: "14%", price: 2200, description: "深色莓果、香草與木質調，口感圓潤，適合搭配烤肉與火腿。" },
  { sku: "AU-BAR-2022", product: "巴羅莎希哈紅葡萄酒", origin: "澳洲・巴羅莎谷", vintage: "2022", volume: "750 ml", alcohol: "14.5%", price: 1800, description: "黑莓、李子與胡椒香氣，風味濃郁，適合搭配炭烤牛肉。" },
  { sku: "NZ-MAR-2023", product: "馬爾堡白蘇維濃白葡萄酒", origin: "紐西蘭・馬爾堡", vintage: "2023", volume: "750 ml", alcohol: "12.5%", price: 1200, description: "柑橘與熱帶水果香氣，酸度清爽，適合搭配海鮮與沙拉。" },
  { sku: "FR-CHP-NV", product: "經典布根地氣泡葡萄酒", origin: "法國・勃根地", vintage: "NV", volume: "750 ml", alcohol: "12%", price: 2600, description: "細緻氣泡伴隨青蘋果與烘焙香氣，適合作為迎賓酒或搭配開胃菜。" },
  { sku: "TW-WHS-NV", product: "臺灣單一麥芽威士忌", origin: "臺灣", vintage: "NV", volume: "700 ml", alcohol: "46%", price: 3200, description: "麥芽、蜂蜜與橡木香氣，口感醇厚，可純飲或加少量水品飲。" },
];

export interface ProductCatalogItem {
  brand?: string;
  name: string;
  vintage?: string;
  country?: string;
  region?: string;
  grape?: string;
  selling_price?: number | string;
  description?: string;
  category?: string;
}

const pick = <T,>(values: readonly T[]): T => values[Math.floor(Math.random() * values.length)];

export function generateRandomOrder(base: WineOrder, name: string, catalog: ProductCatalogItem[] = []): WineOrder {
  const customer = pick(customers.filter((item) => item.name !== base.customer));
  const catalogWines = catalog.filter((item) => item.name?.trim() && Number(item.selling_price) > 0);
  const catalogItem = catalogWines.length ? pick(catalogWines) : null;
  const fallback = pick(wines.filter((item) => item.product !== base.product));
  const wine = catalogItem ? {
    sku: `${catalogItem.brand || "WINE"}-${catalogItem.name}`,
    product: [catalogItem.brand, catalogItem.name].filter(Boolean).join(" "),
    origin: [catalogItem.country, catalogItem.region].filter(Boolean).join("・"),
    vintage: catalogItem.vintage || "NV", volume: "750 ml", alcohol: "—",
    price: Math.max(1, Math.round(Number(catalogItem.selling_price))), description: "",
  } : fallback;
  const minQty = Math.max(1, Math.ceil(50000 / wine.price));
  const maxQty = Math.max(minQty, Math.floor(100000 / wine.price));
  const quantity = minQty + Math.floor(Math.random() * (maxQty - minQty + 1));
  const price = wine.price;
  const total = quantity * price;
  const deposit = Math.round(total * pick([0, 0.2, 0.3, 0.5]));
  const date = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Taipei", year: "numeric", month: "2-digit", day: "2-digit",
  }).format(new Date());
  return {
    ...base, ...wine, date,
    number: `WINE-${date.replaceAll("-", "")}-${crypto.randomUUID().slice(0, 6).toUpperCase()}`,
    author: name || base.author || "訂單專員",
    salesperson: name || base.salesperson || "訂單專員",
    customer: customer.name, contact: customer.contact,
    quantity: String(quantity), price: String(price), deposit: String(deposit),
    status: deposit === 0 ? "待付款" : pick(["已預定", "備貨中"]),
    payment: pick(["銀行轉帳", "信用卡", "現金"]),
    description: "",
    notes: pick([
      "請依訂購瓶數整箱包裝；出貨前請先聯繫收件人確認配送時間。",
      "本批為宴會用酒，請確認瓶身與外盒完整；交貨時間依雙方確認為準。",
      "請核對酒款、年份與瓶數；完成尾款後安排配送。",
      "請分箱包裝並加強防撞保護；如需更改配送時間，請提前聯繫業務人員。",
    ]),
  };
}
